import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../store/auth';
import { toast } from './toast';

export const api = axios.create({
  baseURL: '/api',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** 提取后端返回的可读错误信息 — 用 message 字段，不抛 axios 内部 "Request failed with status code 400" */
function extractErrorMessage(err: AxiosError<any>): string {
  // IM6：429 限流必须在读 respData.message【之前】拦截——后端 ThrottlerException 的 message 是英文
  //   'ThrottlerException: Too Many Requests'，被 GlobalExceptionFilter 原样透传，否则会直接弹给用户。
  if (err.response?.status === 429) return '操作过于频繁，请稍候再试';
  const respData = err.response?.data;
  // 后端 ApiResponse 标准形：{ ok: false, message: 'xxx' } 或 { ok: false, message: ['xxx','yyy'] }
  if (respData) {
    const raw = respData.message;
    // ux2：多字段校验错误全部透传（顿号分隔，最多 3 条防 toast 过长），不再只取首条
    if (Array.isArray(raw) && raw.length > 0) {
      const top = raw.slice(0, 3).map(String);
      return raw.length > 3 ? `${top.join('；')} 等 ${raw.length} 项` : top.join('；');
    }
    if (typeof raw === 'string' && raw.length > 0) return raw;
  }
  // 没有可读 message → 按状态码给通用文案，不暴露 axios 内部字符串
  const status = err.response?.status;
  if (status === 400) return '请求参数有误';
  if (status === 403) return '没有权限执行此操作';
  if (status === 404) return '请求的资源不存在';
  if (status === 409) return '存在冲突，请刷新后重试';
  if (status === 500) return '服务器内部错误';
  if (status && status >= 500) return '服务器异常，请稍后重试';
  if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') return '网络异常，请检查连接';
  return '网络异常，请重试';
}

// 任务 B：统一走 lib/toast 的 dedup 机制（250ms 窗口）
// 任何源（interceptor / mutation.onError / WS 广播）同 message 都会被吞重复
function dedupedToastError(msg: string) {
  toast.error(msg);
}

// IM6：429 限流独立节流——toast 自带 250ms dedup 挡不住持续数秒的限流刷屏。
//   每 5s 最多弹一条黄色 warn(非红 error 降焦虑)，不刷屏也不完全静默。
let last429ToastAt = 0;
const RATE_LIMIT_TOAST_INTERVAL = 5000;

// batch-fix#H1：401 无感续期——用 refreshToken 换新 access token 并重放原请求。
//   并发去重：多请求同时 401 只发一次 refresh(共享 in-flight promise)；用原始 axios(非 api 实例)避免拦截器递归。
let refreshInFlight: Promise<string | null> | null = null;
async function refreshAccessToken(): Promise<string | null> {
  const rt = useAuthStore.getState().refreshToken;
  if (!rt) return null;
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post('/api/auth/refresh', { refreshToken: rt }, { headers: { 'Content-Type': 'application/json' } })
      .then((r) => {
        const d = (r.data && r.data.data) ? r.data.data : r.data;
        if (d && d.accessToken && d.user) {
          useAuthStore.getState().setSession({
            accessToken: d.accessToken, refreshToken: d.refreshToken ?? rt,
            user: d.user, mustChangePassword: !!d.mustChangePassword,
          });
          return d.accessToken as string;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => {
    const data = r.data;
    if (data && data.ok === false) {
      return Promise.reject({ message: data.message, code: data.code });
    }
    return data?.data !== undefined ? { ...r, data: data.data } : r;
  },
  async (err: AxiosError<any>) => {
    if (err.response?.status === 401) {
      const original: any = err.config;
      // 防循环：refresh 端点自身 401 / 已重试过 / 无 config → 直接登出；否则先 refresh 重放一次
      if (original && !original._retried && !(original.url && String(original.url).includes('/auth/refresh'))) {
        original._retried = true;
        const newToken = await refreshAccessToken();
        if (newToken) return api(original); // 重放：请求拦截器自动带上新 token
      }
      useAuthStore.getState().logout();
      window.location.href = '/login';
      const wrapped401: any = new Error('登录已过期，请重新登录');
      wrapped401.handled = true; wrapped401.status = 401;
      return Promise.reject(wrapped401);
    } else if (err.response?.status === 429) {
      // IM6：限流优雅降级——5s 节流弹一条中文 warn，仍 reject 让 query 走退避(main.tsx retry 对 429 返 false)；
      //   wrapped.handled=true 防业务侧再弹、status=429 供 retry 判定。
      const now = Date.now();
      if (now - last429ToastAt > RATE_LIMIT_TOAST_INTERVAL) {
        last429ToastAt = now;
        toast.warn('操作过于频繁，请稍候再试');
      }
      const wrapped: any = new Error('操作过于频繁，请稍候再试');
      wrapped.handled = true;
      wrapped.status = 429;
      wrapped.code = 'RATE_LIMITED';
      return Promise.reject(wrapped);
    } else {
      const msg = extractErrorMessage(err);
      dedupedToastError(msg);
      // P2-5: 用一个干净的 Error 把可读 message 挂上去，并标记 handled。
      // 业务侧 mutation.onError 拿到的就是这个对象，可以读 e.message 但
      // 看到 e.handled=true 就不再 toast 一次。
      const wrapped: any = new Error(msg);
      wrapped.handled = true;
      wrapped.status = err.response?.status;
      wrapped.code = (err.response?.data as any)?.code ?? err.code;
      wrapped.originalError = err;
      return Promise.reject(wrapped);
    }
    return Promise.reject(err);
  },
);

export const $ = {
  get: <T = any>(url: string, params?: any) => api.get<T>(url, { params }).then((r) => r.data as unknown as T),
  post: <T = any>(url: string, body?: any) => api.post<T>(url, body).then((r) => r.data as unknown as T),
  patch: <T = any>(url: string, body?: any) => api.patch<T>(url, body).then((r) => r.data as unknown as T),
  put: <T = any>(url: string, body?: any) => api.put<T>(url, body).then((r) => r.data as unknown as T),
  delete: <T = any>(url: string) => api.delete<T>(url).then((r) => r.data as unknown as T),
};
